import React, { PropsWithChildren, useContext } from 'react'
import { formsAppService, authService } from '@oneblink/apps'
import { FormsAppsTypes } from '@oneblink/types'
import config from '../config'

type AppStyles = Omit<FormsAppsTypes.FormsListStyles, 'menuItems' | 'customCss'>

interface TAppConfigContext extends AppStyles {
  isFetching: boolean
  formsOAuthClientId?: string
}

const AppConfigContext = React.createContext<TAppConfigContext>({
  logoUrl: undefined,
  buttons: {},
  isFetching: true,
})

export function useAppConfig() {
  return useContext(AppConfigContext)
}

export default function AppConfigProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const [stylesState, setStylesState] = React.useState<TAppConfigContext>({
    isFetching: true,
  })

  React.useEffect(() => {
    const abortController = new AbortController()
    const fetchForms = async () => {
      try {
        const { styles, formsOAuthClientId } =
          await formsAppService.getFormsAppConfiguration(
            config.OB_FORMS_APP_ID,
            abortController.signal,
          )

        if (!formsOAuthClientId) {
          throw new Error('Unable to fetch app configuration')
        }
        authService.init({ oAuthClientId: formsOAuthClientId })

        if (abortController.signal.aborted) return
        setStylesState({ ...styles, isFetching: false, formsOAuthClientId })
      } catch (e) {
        if (abortController.signal.aborted) return
        console.error(e)
        setStylesState({ isFetching: false })
      }
    }

    fetchForms()

    return () => {
      abortController.abort()
    }
  }, [])

  if (!stylesState.formsOAuthClientId) return null

  return (
    <AppConfigContext.Provider value={stylesState}>
      {children}
    </AppConfigContext.Provider>
  )
}
